import {
  OnboardStatusDto,
  OnboardingFlowStatusDto,
  OnboardingFlowStepStatusDto,
} from '@app/areas/auth/models/auth-onboarding.dto';

export interface OnboardingFlowStepVm extends OnboardingFlowStepStatusDto {
  isCurrent: boolean;
}

export interface OnboardingFlowVm {
  key: string;
  title: string;
  description: string;
  completedSteps: number;
  totalSteps: number;
  overallPercent: number;
  currentStep: OnboardingFlowStepVm | null;
  steps: OnboardingFlowStepVm[];
}

export function buildOnboardingFlowVm(flow: OnboardingFlowStatusDto | null | undefined): OnboardingFlowVm | null {
  if (!flow) {
    return null;
  }

  const steps = [...(flow.steps ?? [])]
    .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
    .map((step, index) => ({
      ...step,
      sequence: step.sequence ?? index + 1,
    }));

  let currentIndex = steps.findIndex((step) => step.isCurrent);
  if (currentIndex < 0) {
    currentIndex = steps.findIndex((step) => step.status !== 'completed');
  }
  if (currentIndex < 0 && steps.length > 0) {
    currentIndex = steps.length - 1;
  }

  const currentStep = currentIndex >= 0 ? steps[currentIndex] : null;
  const resolvedSteps = steps.map((step, index) => ({
    ...step,
    isCurrent: index === currentIndex,
  }));

  const completedSteps = typeof flow.completedSteps === 'number'
    ? flow.completedSteps
    : resolvedSteps.filter((step) => step.status === 'completed').length;
  const totalSteps = typeof flow.totalSteps === 'number' ? flow.totalSteps : resolvedSteps.length;
  const overallPercent = typeof flow.overallPercent === 'number'
    ? flow.overallPercent
    : totalSteps > 0
      ? Math.round((completedSteps / totalSteps) * 100)
      : 0;

  return {
    key: flow.key,
    title: flow.title,
    description: flow.description ?? '',
    completedSteps,
    totalSteps,
    overallPercent,
    currentStep,
    steps: resolvedSteps,
  };
}

export function markOnboardingStepCompleted(
  status: OnboardStatusDto | null | undefined,
  completedStepKey: string,
): OnboardStatusDto | null {
  if (!status?.currentFlow || !completedStepKey.trim()) {
    return status ?? null;
  }

  const flow = status.currentFlow;
  const steps = [...(flow.steps ?? [])].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
  const completedIndex = steps.findIndex(step => step.key === completedStepKey);

  if (completedIndex < 0) {
    return status;
  }

  const completedAt = new Date().toISOString();
  const updatedSteps = steps.map((step, index) => {
    if (index === completedIndex) {
      return {
        ...step,
        status: 'completed',
        isCurrent: false,
        completedAt,
      };
    }

    if (index === completedIndex + 1) {
      return {
        ...step,
        status: step.status === 'completed' ? step.status : 'in_progress',
        isCurrent: true,
      };
    }

    return {
      ...step,
      isCurrent: false,
    };
  });

  const nextCurrentStep = updatedSteps[completedIndex + 1] ?? updatedSteps[completedIndex] ?? flow.currentStep ?? null;
  const completedSteps = Math.max(
    flow.completedSteps ?? 0,
    updatedSteps.filter(step => step.status === 'completed').length,
  );
  const totalSteps = Math.max(flow.totalSteps ?? 0, updatedSteps.length);
  const overallPercent = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

  return {
    ...status,
    consentRequired: ['app_consent', 'consent', 'ob-consent'].includes(completedStepKey)
      ? []
      : status.consentRequired,
    currentFlow: {
      ...flow,
      completedSteps,
      totalSteps,
      overallPercent,
      currentStep: nextCurrentStep,
      steps: updatedSteps,
    },
  };
}

export function preferOnboardingStatus(
  preferred: OnboardStatusDto | null | undefined,
  fallback: OnboardStatusDto | null | undefined,
): OnboardStatusDto | null {
  if (!preferred) {
    return fallback ?? null;
  }

  if (!fallback) {
    return preferred;
  }

  const preferredFlow = preferred.currentFlow;
  const fallbackFlow = fallback.currentFlow;

  if (!preferredFlow) {
    return fallbackFlow ? fallback : preferred;
  }

  if (!fallbackFlow) {
    return preferred;
  }

  const preferredCompleted = preferredFlow.completedSteps ?? 0;
  const fallbackCompleted = fallbackFlow.completedSteps ?? 0;
  if (preferredCompleted !== fallbackCompleted) {
    return preferredCompleted > fallbackCompleted ? preferred : fallback;
  }

  const preferredCurrentSequence = preferredFlow.currentStep?.sequence ?? 0;
  const fallbackCurrentSequence = fallbackFlow.currentStep?.sequence ?? 0;
  if (preferredCurrentSequence !== fallbackCurrentSequence) {
    return preferredCurrentSequence > fallbackCurrentSequence ? preferred : fallback;
  }

  const preferredPercent = preferredFlow.overallPercent ?? 0;
  const fallbackPercent = fallbackFlow.overallPercent ?? 0;
  if (preferredPercent !== fallbackPercent) {
    return preferredPercent > fallbackPercent ? preferred : fallback;
  }

  const preferredConsentCount = preferred.consentRequired?.length ?? 0;
  const fallbackConsentCount = fallback.consentRequired?.length ?? 0;
  if (preferredConsentCount !== fallbackConsentCount) {
    return preferredConsentCount < fallbackConsentCount ? preferred : fallback;
  }

  return fallback;
}
